import React from 'react';
import { cn } from '../../lib/utils';
import { useBranding } from '../../contexts/BrandingContext';
import { getStatusClasses } from '../../utils/brandingUtils';
import { 
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  ArrowPathIcon,
  PauseIcon,
  PlayIcon,
  StopIcon,
  UserIcon,
  BuildingOfficeIcon,
  DocumentTextIcon,
  CalendarIcon,
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline';

// Status configuration with semantic colors and icons
const STATUS_CONFIG = {
  // Client Statuses
  client: {
    active: {
      label: 'Active',
      icon: CheckCircleIcon,
      color: 'emerald',
      bgColor: 'bg-emerald-50',
      textColor: 'text-emerald-700',
      borderColor: 'border-emerald-200',
      dotColor: 'bg-emerald-500'
    },
    prospect: {
      label: 'Prospect',
      icon: UserIcon,
      color: 'blue',
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-700',
      borderColor: 'border-blue-200',
      dotColor: 'bg-blue-500'
    },
    inactive: {
      label: 'Inactive',
      icon: PauseIcon,
      color: 'gray',
      bgColor: 'bg-muted',
      textColor: 'text-foreground',
      borderColor: 'border-border',
      dotColor: 'bg-muted'
    },
    archived: {
      label: 'Archived',
      icon: StopIcon,
      color: 'slate',
      bgColor: 'bg-slate-50',
      textColor: 'text-slate-700',
      borderColor: 'border-slate-200',
      dotColor: 'bg-slate-500'
    }
  },
  
  // Task Statuses
  task: {
    not_started: {
      label: 'Not Started',
      icon: ClockIcon,
      color: 'gray',
      bgColor: 'bg-muted',
      textColor: 'text-foreground',
      borderColor: 'border-border',
      dotColor: 'bg-muted'
    },
    in_progress: {
      label: 'In Progress',
      icon: ArrowPathIcon,
      color: 'blue',
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-700',
      borderColor: 'border-blue-200',
      dotColor: 'bg-blue-500'
    },
    completed: {
      label: 'Completed',
      icon: CheckCircleIcon,
      color: 'emerald',
      bgColor: 'bg-emerald-50',
      textColor: 'text-emerald-700',
      borderColor: 'border-emerald-200',
      dotColor: 'bg-emerald-500'
    },
    on_hold: {
      label: 'On Hold',
      icon: PauseIcon,
      color: 'amber',
      bgColor: 'bg-amber-50',
      textColor: 'text-amber-700',
      borderColor: 'border-amber-200',
      dotColor: 'bg-amber-500'
    },
    cancelled: {
      label: 'Cancelled',
      icon: XMarkIcon,
      color: 'red',
      bgColor: 'bg-red-50',
      textColor: 'text-red-700',
      borderColor: 'border-red-200',
      dotColor: 'bg-red-500'
    },
    blocked: {
      label: 'Blocked',
      icon: ExclamationTriangleIcon,
      color: 'red',
      bgColor: 'bg-red-50',
      textColor: 'text-red-700',
      borderColor: 'border-red-200',
      dotColor: 'bg-red-500'
    }
  },

  // Priority Levels
  priority: {
    low: {
      label: 'Low',
      icon: ClockIcon,
      color: 'gray',
      bgColor: 'bg-muted',
      textColor: 'text-foreground',
      borderColor: 'border-border',
      dotColor: 'bg-muted'
    },
    medium: {
      label: 'Medium',
      icon: ExclamationTriangleIcon,
      color: 'amber',
      bgColor: 'bg-amber-50',
      textColor: 'text-amber-700',
      borderColor: 'border-amber-200',
      dotColor: 'bg-amber-500'
    },
    high: {
      label: 'High',
      icon: ExclamationTriangleIcon,
      color: 'orange',
      bgColor: 'bg-orange-50',
      textColor: 'text-orange-700',
      borderColor: 'border-orange-200',
      dotColor: 'bg-orange-500'
    },
    urgent: {
      label: 'Urgent',
      icon: ExclamationTriangleIcon,
      color: 'red',
      bgColor: 'bg-red-50',
      textColor: 'text-red-700',
      borderColor: 'border-red-200',
      dotColor: 'bg-red-500'
    }
  },

  // Document Statuses
  document: {
    approved: {
      label: 'Approved',
      icon: CheckCircleIcon,
      color: 'emerald',
      bgColor: 'bg-emerald-50',
      textColor: 'text-emerald-700',
      borderColor: 'border-emerald-200',
      dotColor: 'bg-emerald-500'
    },
    pending: {
      label: 'Pending',
      icon: ClockIcon,
      color: 'amber',
      bgColor: 'bg-amber-50',
      textColor: 'text-amber-700',
      borderColor: 'border-amber-200',
      dotColor: 'bg-amber-500'
    },
    rejected: {
      label: 'Rejected',
      icon: XMarkIcon,
      color: 'red',
      bgColor: 'bg-red-50',
      textColor: 'text-red-700',
      borderColor: 'border-red-200',
      dotColor: 'bg-red-500'
    },
    draft: {
      label: 'Draft',
      icon: DocumentTextIcon,
      color: 'gray',
      bgColor: 'bg-muted',
      textColor: 'text-foreground',
      borderColor: 'border-border',
      dotColor: 'bg-muted'
    }
  },

  // Campaign Statuses
  campaign: {
    draft: {
      label: 'Draft',
      icon: DocumentTextIcon,
      color: 'gray',
      bgColor: 'bg-gray-100',
      textColor: 'text-gray-700',
      borderColor: 'border-gray-200',
      dotColor: 'bg-gray-500'
    },
    scheduled: {
      label: 'Scheduled',
      icon: CalendarIcon,
      color: 'blue',
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-700',
      borderColor: 'border-blue-200',
      dotColor: 'bg-blue-500'
    },
    running: {
      label: 'Running',
      icon: PlayIcon,
      color: 'emerald',
      bgColor: 'bg-emerald-50',
      textColor: 'text-emerald-700',
      borderColor: 'border-emerald-200',
      dotColor: 'bg-emerald-500'
    },
    paused: {
      label: 'Paused',
      icon: PauseIcon,
      color: 'amber',
      bgColor: 'bg-amber-50',
      textColor: 'text-amber-700',
      borderColor: 'border-amber-200',
      dotColor: 'bg-amber-500'
    },
    completed: {
      label: 'Completed',
      icon: CheckCircleIcon,
      color: 'emerald',
      bgColor: 'bg-emerald-50',
      textColor: 'text-emerald-700',
      borderColor: 'border-emerald-200',
      dotColor: 'bg-emerald-500'
    },
    cancelled: {
      label: 'Cancelled',
      icon: XMarkIcon,
      color: 'red',
      bgColor: 'bg-red-50',
      textColor: 'text-red-700',
      borderColor: 'border-red-200',
      dotColor: 'bg-red-500'
    }
  }
};

// Enhanced Status Badge Component
export function StatusBadge({ 
  status, 
  type = 'task', 
  size = 'default',
  showIcon = true,
  showDot = false,
  className = '',
  ...props 
}) {
  const { branding } = useBranding();
  // Use appropriate fallback based on type
  const fallbackConfig = type === 'campaign' 
    ? STATUS_CONFIG.campaign.draft 
    : STATUS_CONFIG.task.not_started;
  const config = STATUS_CONFIG[type]?.[status] || fallbackConfig;
  const Icon = config.icon;

  const sizeClasses = {
    sm: 'px-2 py-0.5 text-xs',
    default: 'px-2.5 py-1 text-sm',
    lg: 'px-3 py-1.5 text-base'
  };

  const iconSizes = {
    sm: 'h-3 w-3',
    default: 'h-4 w-4',
    lg: 'h-5 w-5'
  };

  // Use branding-aware status classes
  const statusClasses = getStatusClasses(status, type, branding);

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border font-medium transition-colors',
        'focus:outline-none focus:ring-2 focus:ring-offset-1',
        statusClasses,
        sizeClasses[size],
        className
      )}
      role="status"
      aria-label={`Status: ${config.label}`}
      {...props}
    >
      {showDot && (
        <span 
          className={cn('h-2 w-2 rounded-full', config.dotColor)}
          aria-hidden="true"
        />
      )}
      {showIcon && (
        <Icon 
          className={cn(iconSizes[size], 'shrink-0')}
          aria-hidden="true"
        />
      )}
      <span className="truncate">{config.label}</span>
    </span>
  );
}

// Compact Status Indicator (just dot + text)
export function StatusIndicator({ 
  status, 
  type = 'task', 
  className = '',
  ...props 
}) {
  const { branding } = useBranding();
  const config = STATUS_CONFIG[type]?.[status] || STATUS_CONFIG.task.not_started;

  // Use branding-aware status classes
  const statusClasses = getStatusClasses(status, type, branding);

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 text-sm font-medium',
        statusClasses,
        className
      )}
      role="status"
      aria-label={`Status: ${config.label}`}
      {...props}
    >
      <span 
        className={cn('h-2 w-2 rounded-full', config.dotColor)}
        aria-hidden="true"
      />
      {config.label}
    </span>
  );
}

// Status Dropdown Component
export function StatusDropdown({ 
  value, 
  onChange, 
  type = 'task',
  size = 'default',
  className = '',
  disabled = false,
  ...props 
}) {
  const { branding } = useBranding();
  const configs = STATUS_CONFIG[type] || STATUS_CONFIG.task;
  const currentConfig = configs[value] || configs.not_started;

  const sizeClasses = {
    sm: 'px-2 py-1 text-xs',
    default: 'px-3 py-2 text-sm',
    lg: 'px-4 py-2.5 text-base'
  };

  // Use branding-aware status classes
  const statusClasses = getStatusClasses(value, type, branding);

  return (
    <select
      value={value}
      onChange={onChange}
      disabled={disabled}
      className={cn(
        'rounded-md border font-medium transition-colors',
        'bg-background text-foreground',
        'focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        statusClasses,
        sizeClasses[size],
        className
      )}
      aria-label={`Select ${type} status`}
      {...props}
    >
      {Object.entries(configs).map(([key, config]) => {
        const Icon = config.icon;
        return (
          <option key={key} value={key} className={config.textColor}>
            {config.label}
          </option>
        );
      })}
    </select>
  );
}

// Status Progress Bar
export function StatusProgress({ 
  status, 
  type = 'task',
  showLabel = true,
  className = '',
  ...props 
}) {
  const config = STATUS_CONFIG[type]?.[status] || STATUS_CONFIG.task.not_started;
  
  // Calculate progress percentage based on status
  const progressMap = {
    task: {
      not_started: 0,
      in_progress: 50,
      completed: 100,
      on_hold: 25,
      cancelled: 0,
      blocked: 0
    },
    client: {
      prospect: 25,
      active: 100,
      inactive: 0,
      archived: 0
    }
  };

  const progress = progressMap[type]?.[status] || 0;

  return (
    <div className={cn('space-y-1', className)} {...props}>
      {showLabel && (
        <div className="flex items-center justify-between text-sm">
          <span className="font-medium text-foreground">{config.label}</span>
          <span className="text-muted-foreground">{progress}%</span>
        </div>
      )}
      <div className="w-full bg-muted rounded-full h-2">
        <div
          className={cn(
            'h-2 rounded-full transition-all duration-300',
            config.bgColor.replace('bg-', 'bg-').replace('-50', '-500')
          )}
          style={{ width: `${progress}%` }}
          role="progressbar"
          aria-valuenow={progress}
          aria-valuemin="0"
          aria-valuemax="100"
          aria-label={`Progress: ${progress}%`}
        />
      </div>
    </div>
  );
}

// Status Filter Component
export function StatusFilter({ 
  selectedStatuses = [], 
  onStatusChange, 
  type = 'task',
  className = '',
  ...props 
}) {
  const configs = STATUS_CONFIG[type] || STATUS_CONFIG.task;

  return (
    <div className={cn('flex flex-wrap gap-2', className)} {...props}>
      {Object.entries(configs).map(([key, config]) => {
        const isSelected = selectedStatuses.includes(key);
        const Icon = config.icon;
        
        return (
          <button
            key={key}
            onClick={() => onStatusChange(key)}
            className={cn(
              'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-sm font-medium transition-colors',
              'focus:outline-none focus:ring-2 focus:ring-offset-1',
              isSelected
                ? cn(config.bgColor, config.textColor, config.borderColor)
                : 'bg-card text-foreground border-border hover:bg-muted'
            )}
            aria-pressed={isSelected}
            aria-label={`Filter by ${config.label} status`}
          >
            <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
            {config.label}
          </button>
        );
      })}
    </div>
  );
}

// Export configuration for external use
export { STATUS_CONFIG };

